import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  deleteDocumentBundle,
  deleteStandardDocument,
  fetchStandardDocument,
  listDocumentBundles,
  listStandardDocuments,
  listStandards,
  readPlatformConfig,
  saveDocumentBundle,
  savePlatformConfig,
  uploadStandardDocuments,
  type BulkUploadOutcome,
  type DocumentBundle,
  type DocumentBundleList,
  type PlatformSettings,
  type StandardDocument,
  type StandardDocumentList,
} from "../api/client";
import { AuditPersona } from "./AuditPersona";
import { Callout } from "../components/Callout";
import { DateField } from "../components/DateField";
import { TextField } from "../components/TextField";

/**
 * Screen 73 — platform configuration, and the number the prototype gets wrong.
 *
 * The prototype's threshold field is prefilled **75**, its help text says 75 and its error
 * says 75. IAF MD 5 Cl. 2.1.2 says 80, and so does `durationGuardrails.durationFloor`. The
 * plan's whole instruction for this task is *"Threshold floor 80"*, and the fix is not to
 * type 80 into the field: it is to stop the browser holding an opinion about the limit at
 * all. `regulatory_floor` arrives with the values and the field is validated against it, so
 * there is one number and the server owns it — the same rule as every regulatory answer on
 * the intake form.
 *
 * **Two fields, two different kinds of thing.** The follow-up cap is configuration: an
 * administrator moves it and the next session to open stamps the new value. The threshold
 * floor is regulation with a configurable strictness — it may be raised above 80 and never
 * lowered below it, which is precisely the permission a certification body holds against the
 * platform floor one level down.
 *
 * **Changing either alters nothing already decided**, and that is true because of where each
 * value is stamped rather than because this screen promises it. The cap lands on the session
 * row when the session opens; the floor lands in the duration calculation. A session already
 * conducted keeps the cap it ran under, and a determination already made keeps the floor it
 * was made against.
 *
 * Both inputs are `type="text"` with `inputmode="numeric"` — `type="number"` returns null
 * from `selectionStart` and lets a scroll wheel silently change a value that feeds a
 * duration determination.
 *
 * **The third card is not a form.** The standards corpus sits below Save and acts
 * immediately, because an upload cannot join the transaction the two numbers share and a
 * file waiting for a button already pressed is a file the administrator believes is stored
 * and is not. It is also **not a standards catalogue** — see `StandardsCorpus` below.
 */
export function PlatformConfiguration({
  onOpenCosting,
}: {
  onOpenCosting: () => void;
}) {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [cap, setCap] = useState("");
  const [floor, setFloor] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [corpus, setCorpus] = useState<StandardDocumentList | null>(null);
  const [bundles, setBundles] = useState<DocumentBundleList | null>(null);
  const [standards, setStandardsList] = useState<string[]>([]);

  // Read separately from the settings, because they are separate resources and a corpus
  // that fails to load must not stop the two numbers being editable.
  const reloadCorpus = useCallback(() => {
    listStandardDocuments()
      .then(setCorpus)
      .catch(() => setCorpus(null));
  }, []);

  const reloadBundles = useCallback(() => {
    listDocumentBundles()
      .then(setBundles)
      .catch(() => setBundles(null));
  }, []);

  useEffect(() => {
    readPlatformConfig()
      .then(adopt)
      .catch((cause: unknown) =>
        setError(
          cause instanceof ApiError
            ? cause.message
            : "The platform configuration could not be read.",
        ),
      );
    reloadCorpus();
    reloadBundles();
    listStandards()
      .then(setStandardsList)
      .catch(() => setStandardsList([]));
  }, [reloadCorpus, reloadBundles]);

  function adopt(next: PlatformSettings) {
    setSettings(next);
    setCap(String(next.follow_up_cap));
    setFloor(String(next.duration_threshold_floor));
  }

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      adopt(
        await savePlatformConfig({
          followUpCap: Number(cap),
          durationThresholdFloor: Number(floor),
        }),
      );
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The change was not saved.");
    } finally {
      setBusy(false);
    }
  };

  // Checked against the server's own bounds rather than against a limit typed here. The
  // route refuses both of these anyway; the point of checking is to say why before the trip.
  const capError =
    settings && cap !== "" && !(Number(cap) >= settings.minimum_follow_up_cap)
      ? `At least ${settings.minimum_follow_up_cap}. At nought every clause would close with a Nonconformity unanswered.`
      : undefined;
  const floorError =
    settings && floor !== "" && Number(floor) < settings.regulatory_floor
      ? `IAF MD 5 Cl. 2.1.2 puts the floor at ${settings.regulatory_floor}%. A stricter platform floor is permitted; a looser one is not.`
      : settings && Number(floor) > 100
        ? "A floor above 100% is one no audit could meet."
        : undefined;

  return (
    <>
      <div className="between" style={{ marginBottom: "16px" }}>
        <div>
          <div className="eyebrow">Complao admin</div>
          <h1>Platform configuration</h1>
          <p className="sub">
            Applies to every certification body. Audits take the values current when their
            session opens and their determination is made.
          </p>
        </div>
        {settings ? (
          <div style={{ textAlign: "right" }} className="sub">
            {settings.updated_by ? (
              <>
                Last changed by {settings.updated_by}
                <br />
                {stamp(settings.updated_at)}
              </>
            ) : (
              <>Never changed since the platform was seeded.</>
            )}
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {/* The corpus is not part of the configuration form: uploading and deleting take
          effect immediately, so it sits above the form rather than beside its Save button. */}
      {corpus === null ? (
        <div className="card">Reading the standards corpus&hellip;</div>
      ) : (
        <StandardsCorpus
          documents={corpus.values}
          bundles={bundles?.values ?? []}
          standards={standards}
          onDocumentsChanged={reloadCorpus}
          onBundlesChanged={reloadBundles}
        />
      )}

      {/* The persona is the third thing on this screen that acts on its own rather than
          through Save, and for a different reason from the corpus: publishing appends a
          version, and a version is not a field on a form. It sits above the two numbers
          because it is the larger of the two things an administrator comes here to change. */}
      <AuditPersona />

      {/* M34. A link out rather than a card of figures inlined here, and the distinction is
          not cosmetic: everything else on this screen is *configuration* — values an
          administrator sets that alter what later audits do. Costing sets nothing. It is a
          read, and an expensive one that writes an access event to every certification
          body's chain each time it runs. Rendering it inline would fire that write on every
          visit to screen 73, including the visits that came to change the follow-up cap. */}
      <div className="card">
        <div className="between">
          <div>
            <h2>Costing</h2>
            <p className="hint" style={{ margin: 0 }}>
              What each audit consumed, across every certification body. Opening it records
              a read on each body&rsquo;s own trail.
            </p>
          </div>
          <button type="button" className="btn" onClick={onOpenCosting}>
            Open costing
          </button>
        </div>
      </div>

      <Callout tone="warn" title="Changing these does not alter audits already planned.">
        {settings?.applies_from}
      </Callout>

      {settings === null ? (
        <div className="card">Reading the configuration&hellip;</div>
      ) : (
        <>
          <div className="card">
            <h2>Audit session</h2>
            <div className="grid2">
              <div>
                <TextField
                  id="config-cap"
                  label="Follow-up cap"
                  hint="Attempts before Complao records the gap and advances."
                  value={cap}
                  onChange={setCap}
                  kind="numeric"
                  error={capError}
                  required
                />
                <p className="sub" style={{ fontSize: "11.5px" }}>
                  A vague answer triggers a narrower question. At the cap the clause is closed
                  with a Minor&nbsp;NC, marked as the policy&rsquo;s rather than as a
                  judgement, and the reason is written to the trail. &ldquo;I don&rsquo;t
                  know&rdquo; is treated as a complete answer and consumes no attempt.
                </p>
              </div>
              <div>
                <label className="f">Pause behaviour</label>
                <div className="plan-ed" style={{ fontSize: "12.5px", lineHeight: 1.6 }}>
                  The audit runs on hours, not days. The lead auditor pauses for a break or at
                  the end of a day; the clock stops, nothing can be sent, and the
                  auditee&rsquo;s access link stays valid until the audit closes. Not
                  configurable &mdash; it is the behaviour, not a setting.
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2>Duration</h2>
            <div style={{ maxWidth: "360px" }}>
              <TextField
                id="config-floor"
                label="Threshold floor"
                hint="% of the calculated audit time."
                value={floor}
                onChange={setFloor}
                kind="numeric"
                error={floorError}
                required
              />
            </div>
            <p className="sub" style={{ fontSize: "11.5px" }}>
              IAF&nbsp;MD&nbsp;5 Cl.&nbsp;2.1.2 puts this at{" "}
              <b>{settings.regulatory_floor}%</b>, and it is a <i>should</i> &mdash; an
              agreed duration below it is permitted, warned about and recorded as a
              departure, never refused. A certification body may set a stricter threshold
              than this floor and never a looser one, and neither may this screen.
            </p>
          </div>

          {saved ? (
            <Callout tone="info" title="Saved.">
              The new values apply to the next session that opens and the next determination
              that is made. Nothing already recorded has moved.
            </Callout>
          ) : null}

          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn"
              disabled={busy || Boolean(capError) || Boolean(floorError)}
              onClick={() => void save()}
            >
              {busy ? "Saving…" : "Save configuration"}
            </button>
          </div>
        </>
      )}
    </>
  );
}

function stamp(value: string): string {
  return value.slice(0, 16).replace("T", " ");
}

/**
 * Screen 73's third card — the reference documents Complao holds, and how they map to the
 * standards Complao audits (M12 redesign).
 *
 * **It is not a standards catalogue.** The three standards still come from
 * `GET /reference/standards`, the same route the intake form's chips use, and there is no
 * field anywhere on this card that adds a fourth. A standard added by name would carry no
 * base table, no complexity default, no clause library and no document checklist, and an
 * audit determined against it would read Table QMS 1 — the defect ADR 0002 exists to
 * prevent.
 *
 * **Documents are one flat pool.** There is no more `requirement`/`method` split at upload —
 * every document is a row in `DocumentTable`, each provisioning its own Bedrock knowledge
 * base. Which standard, or combination of standards, a document answers for is decided
 * afterwards, in `DocumentBundles` below: a document is retrieved for a live session only
 * where it is a named member of the bundle matching that audit's exact combination — there
 * is no automatic "applies to every audit" category any more.
 */
export function StandardsCorpus({
  documents,
  bundles,
  standards,
  onDocumentsChanged,
  onBundlesChanged,
}: {
  documents: StandardDocument[];
  bundles: DocumentBundle[];
  standards: string[];
  onDocumentsChanged: () => void;
  onBundlesChanged: () => void;
}) {
  return (
    <div className="card">
      <h2>Standards</h2>
      <p className="sub" style={{ fontSize: "11.5px", marginTop: "-4px" }}>
        The documents that define the standards, and the documents that govern how a Stage 1
        audit is conducted, held once for the platform in one pool. Uploading and deleting
        take effect immediately &mdash; they are not part of the configuration form below and
        the Save button does not govern them. Complao administrators only: a certification
        body has no route to these files.
      </p>
      <DocumentTable documents={documents} onChanged={onDocumentsChanged} />
      <DocumentBundles
        bundles={bundles}
        documents={documents}
        standards={standards}
        onChanged={onBundlesChanged}
      />
      <p className="sub" style={{ fontSize: "11.5px" }}>
        Which standards appear here is not a setting. Each one needs a base table, a
        complexity default, a clause library and a document checklist before an audit can be
        determined against it, so a fourth arrives with its reference data or not at all.
      </p>
    </div>
  );
}

/** One chosen-but-not-yet-uploaded file, and the metadata staged against it. */
type StagedDocument = {
  id: string;
  file: File;
  edition: string;
  publishedOn: string;
  licenceReference: string;
  licenceHolder: string;
};

/**
 * The flat document list, and the staging area above it — "Choose documents" picks any
 * number of files at once; each is edited in place (Edition / Published on / Licence
 * reference / Licence held by, all optional) before one "Upload" click submits the whole
 * staged set together. Mirrors the auditee portal's own bulk upload (M15-1,
 * `AuditeePortal.tsx`'s `Documents`) for the choose/stage/remove/submit mechanic; this
 * screen adds inline per-file metadata editing, since every field here is meaningful per
 * document in a way the auditee's upload has no equivalent of.
 */
function DocumentTable({
  documents,
  onChanged,
}: {
  documents: StandardDocument[];
  onChanged: () => void;
}) {
  const [staged, setStaged] = useState<StagedDocument[]>([]);
  const [refusals, setRefusals] = useState<BulkUploadOutcome[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const picker = useRef<HTMLInputElement | null>(null);

  const stage = (chosen: FileList | null) => {
    if (!chosen || chosen.length === 0) return;
    setStaged((current) => [
      ...current,
      ...Array.from(chosen).map((file, index) => ({
        id: `${file.name}-${current.length + index}-${file.lastModified}`,
        file,
        edition: "",
        publishedOn: "",
        licenceReference: "",
        licenceHolder: "",
      })),
    ]);
    // Reset so re-picking the same filename fires `change` again — otherwise the browser
    // considers the value unchanged and the handler never runs.
    if (picker.current) picker.current.value = "";
  };

  const remove = (id: string) =>
    setStaged((current) => current.filter((entry) => entry.id !== id));

  const update = (id: string, patch: Partial<StagedDocument>) =>
    setStaged((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
    );

  const submit = async () => {
    if (staged.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    setRefusals([]);
    try {
      const result = await uploadStandardDocuments(
        staged.map((entry) => ({
          file: entry.file,
          edition: entry.edition.trim() || undefined,
          publishedOn: entry.publishedOn || undefined,
          licenceReference: entry.licenceReference.trim() || undefined,
          licenceHolder: entry.licenceHolder.trim() || undefined,
        })),
      );
      const failed = result.results.filter((entry) => !entry.accepted);
      setRefusals(failed);
      // Only the accepted ones leave the tray — a file the server refused stays in front of
      // the admin with the reason beside it, so they can fix it rather than discover later
      // that it never arrived. Matches the auditee portal's own submit() exactly.
      const kept = new Set(failed.map((entry) => entry.filename));
      setStaged((current) => current.filter((entry) => kept.has(entry.file.name)));
      onChanged();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The documents were not stored.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="seccard" style={{ marginBottom: "12px" }}>
      <div className="between" style={{ alignItems: "baseline" }}>
        <b>Documents</b>
        <span className="sub" style={{ fontSize: "11px" }}>
          {documents.length === 0 ? "No document held" : `${documents.length} held`}
        </span>
      </div>

      {documents.map((document) => (
        <DocumentRow key={document.id} document={document} onChanged={onChanged} />
      ))}

      <input
        type="file"
        multiple
        hidden
        ref={picker}
        onChange={(event) => stage(event.target.files)}
      />
      <div className="row" style={{ gap: "10px", alignItems: "center", marginTop: "10px" }}>
        <button
          type="button"
          className="btn sec sm"
          disabled={submitting}
          onClick={() => picker.current?.click()}
        >
          Choose documents
        </button>
        <span className="sub" style={{ fontSize: "11px" }}>
          PDF, DOCX or TXT · up to 50 MB each, 50 at a time
        </span>
      </div>

      {staged.map((entry) => (
        <StagedRow
          key={entry.id}
          entry={entry}
          disabled={submitting}
          onChange={(patch) => update(entry.id, patch)}
          onRemove={() => remove(entry.id)}
        />
      ))}

      {refusals.length > 0 ? (
        <Callout tone="warn" title="These were not accepted.">
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            {refusals.map((entry) => (
              <li key={entry.filename}>
                <b>{entry.filename}</b> — {entry.reason}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {error ? (
        <p className="err" role="alert">
          {error}
        </p>
      ) : null}

      {staged.length > 0 ? (
        <div
          className="row"
          style={{ justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}
        >
          <span className="sub" style={{ fontSize: "11px" }}>
            {`${staged.length} document${staged.length === 1 ? "" : "s"} ready to submit.`}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={submitting}
            onClick={() => void submit()}
          >
            {submitting
              ? "Uploading…"
              : `Upload ${staged.length} document${staged.length === 1 ? "" : "s"}`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function StagedRow({
  entry,
  disabled,
  onChange,
  onRemove,
}: {
  entry: StagedDocument;
  disabled: boolean;
  onChange: (patch: Partial<StagedDocument>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="dcard">
      <div className="dn">
        <b>{entry.file.name}</b>
        <button type="button" className="btn sec sm" disabled={disabled} onClick={onRemove}>
          Remove
        </button>
      </div>
      <div className="grid2">
        <TextField
          id={`staged-edition-${entry.id}`}
          label="Edition"
          hint="Shown beside the filename. Optional."
          value={entry.edition}
          onChange={(value) => onChange({ edition: value })}
          placeholder="2015"
        />
        <DateField
          id={`staged-published-${entry.id}`}
          label="Published on"
          value={entry.publishedOn}
          onChange={(value) => onChange({ publishedOn: value })}
        />
      </div>
      <div className="grid2">
        <TextField
          id={`staged-licence-ref-${entry.id}`}
          label="Licence reference"
          hint="Optional, but travels with licence held by."
          value={entry.licenceReference}
          onChange={(value) => onChange({ licenceReference: value })}
        />
        <TextField
          id={`staged-licence-holder-${entry.id}`}
          label="Licence held by"
          value={entry.licenceHolder}
          onChange={(value) => onChange({ licenceHolder: value })}
        />
      </div>
    </div>
  );
}

//: Reuses the platform's existing pill classes rather than inventing a new one — `failed` is
//: an infrastructure state, not a finding, and finding colours are never reused elsewhere.
const CORPUS_STATE_LABEL: Record<string, { text: string; pill: string }> = {
  ready: { text: "Ready", pill: "pill p-ok" },
  provisioning: { text: "Provisioning…", pill: "pill p-wait" },
  failed: { text: "Not searchable", pill: "pill p-off" },
};

function DocumentRow({
  document: entry,
  onChanged,
}: {
  document: StandardDocument;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const state = CORPUS_STATE_LABEL[entry.corpus_state] ?? {
    text: entry.corpus_state,
    pill: "pill",
  };

  const open = async () => {
    setError(null);
    try {
      window.open(await fetchStandardDocument(entry.id), "_blank", "noopener");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The file is not readable.");
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        `Delete ${entry.filename}? This removes the file, its record, and its knowledge ` +
          "base. It cannot be undone.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deleteStandardDocument(entry.id);
      onChanged();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The document was not deleted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dcard">
      <div className="dn">
        <b>{entry.filename}</b>
        <div className="row" style={{ gap: "6px", flex: "none" }}>
          <span className={state.pill} title={entry.corpus_failure ?? undefined}>
            {state.text}
          </span>
          <button type="button" className="btn sec sm" onClick={() => void open()}>
            Open
          </button>
          <button
            type="button"
            className="btn sec sm"
            disabled={busy}
            onClick={() => void remove()}
          >
            Delete
          </button>
        </div>
      </div>
      <div className="dmeta">
        {kilobytes(entry.size_bytes)}
        {entry.edition ? ` · edition ${entry.edition}` : ""}
        {entry.published_on ? ` · published ${entry.published_on}` : ""} · uploaded by{" "}
        {entry.uploaded_by} {stamp(entry.uploaded_at)}
      </div>
      {entry.licence_reference || entry.licence_holder ? (
        <div className="dmeta">
          Licence {entry.licence_reference ?? "—"} · held by {entry.licence_holder ?? "—"}
        </div>
      ) : null}

      {error ? (
        <p className="err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The what-and-how mapping: which combination of standards names which documents.
 *
 * A combination also stays listed when every one of its documents is deleted elsewhere —
 * that alone never removes it, so it stays addressable for an administrator to re-populate.
 * Deleting the combination itself is a separate, explicit action below.
 */
function DocumentBundles({
  bundles,
  documents,
  standards,
  onChanged,
}: {
  bundles: DocumentBundle[];
  documents: StandardDocument[];
  standards: string[];
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const byId = new Map(documents.map((document) => [document.id, document]));

  return (
    <div className="seccard">
      <div className="between" style={{ alignItems: "baseline" }}>
        <b>Standard combinations</b>
        <span className="sub" style={{ fontSize: "11px" }}>
          {bundles.length === 0 ? "No combination held" : `${bundles.length} held`}
        </span>
      </div>
      <p className="sub" style={{ fontSize: "11px", marginTop: "-2px" }}>
        Which documents apply to a standard, or a combination of standards. A document is
        never included by default &mdash; even one that governs how the audit is conducted
        must be added to every combination it should apply to.
      </p>

      {bundles.map((bundle) =>
        editing === bundle.id ? (
          <BundleEditor
            key={bundle.id}
            bundle={bundle}
            documents={documents}
            standards={standards}
            onDone={() => {
              setEditing(null);
              onChanged();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <BundleRow
            key={bundle.id}
            bundle={bundle}
            byId={byId}
            onEdit={() => setEditing(bundle.id)}
            onChanged={onChanged}
          />
        ),
      )}

      {adding ? (
        <BundleEditor
          bundle={null}
          documents={documents}
          standards={standards}
          onDone={() => {
            setAdding(false);
            onChanged();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <div className="row" style={{ marginTop: "10px" }}>
          <button type="button" className="btn sec sm" onClick={() => setAdding(true)}>
            Add a combination
          </button>
        </div>
      )}
    </div>
  );
}

function BundleRow({
  bundle,
  byId,
  onEdit,
  onChanged,
}: {
  bundle: DocumentBundle;
  byId: Map<string, StandardDocument>;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    const named = bundle.standards.join(" + ");
    if (
      !window.confirm(
        `Delete the combination for ${named}? Its documents are not deleted — only this ` +
          "grouping. It cannot be undone.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deleteDocumentBundle(bundle.id);
      onChanged();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The combination was not deleted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dcard">
      <div className="dn">
        <div className="chips">
          {bundle.standards.map((standard) => (
            <span key={standard} className="chip" aria-pressed="true">
              {standard}
            </span>
          ))}
        </div>
        <div className="row" style={{ gap: "6px", flex: "none" }}>
          <span className="sub" style={{ fontSize: "11px" }}>
            {bundle.document_ids.length} document
            {bundle.document_ids.length === 1 ? "" : "s"}
          </span>
          <button type="button" className="btn sec sm" onClick={onEdit} disabled={busy}>
            Edit
          </button>
          <button
            type="button"
            className="btn sec sm"
            disabled={busy}
            onClick={() => void remove()}
          >
            Delete
          </button>
        </div>
      </div>
      <div className="dmeta">
        {bundle.document_ids.length === 0
          ? "No documents assigned"
          : bundle.document_ids
              .map((id) => byId.get(id)?.filename ?? "(deleted document)")
              .join(", ")}
      </div>
      {error ? (
        <p className="err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function BundleEditor({
  bundle,
  documents,
  standards,
  onDone,
  onCancel,
}: {
  /** `null` when creating a new combination. */
  bundle: DocumentBundle | null;
  documents: StandardDocument[];
  standards: string[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [selectedStandards, setSelectedStandards] = useState<string[]>(
    bundle?.standards ?? [],
  );
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>(
    bundle?.document_ids ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggleStandard = (standard: string) => {
    setSelectedStandards((current) =>
      current.includes(standard)
        ? current.filter((one) => one !== standard)
        : [...current, standard],
    );
  };

  const toggleDocument = (documentId: string) => {
    setSelectedDocuments((current) =>
      current.includes(documentId)
        ? current.filter((one) => one !== documentId)
        : [...current, documentId],
    );
  };

  const ready = selectedStandards.length > 0 && selectedDocuments.length > 0;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveDocumentBundle(selectedStandards, selectedDocuments, bundle?.id);
      onDone();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The combination was not saved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stdupload">
      <label className="f">
        Standards <b>*</b>
      </label>
      <div className="chips">
        {standards.map((standard) => (
          <button
            key={standard}
            type="button"
            className="chip"
            aria-pressed={selectedStandards.includes(standard)}
            onClick={() => toggleStandard(standard)}
          >
            {standard}
          </button>
        ))}
      </div>

      <label className="f" style={{ marginTop: "10px" }}>
        Documents <b>*</b>
      </label>
      {documents.length === 0 ? (
        <p className="sub" style={{ fontSize: "11px" }}>
          Upload a document above before assembling a combination.
        </p>
      ) : (
        documents.map((document) => (
          <label key={document.id} className="row" style={{ gap: "8px", fontSize: "12.5px" }}>
            <input
              type="checkbox"
              checked={selectedDocuments.includes(document.id)}
              onChange={() => toggleDocument(document.id)}
            />
            {document.filename}
          </label>
        ))
      )}

      {error ? (
        <p className="err" role="alert">
          {error}
        </p>
      ) : null}

      <div className="row" style={{ gap: "8px", marginTop: "10px" }}>
        <button
          type="button"
          className="btn sm"
          disabled={busy || !ready}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="btn sec sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function kilobytes(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}
